import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useSubscription } from "../SubscriptionContext";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { FaQuestionCircle, FaCrown } from "react-icons/fa";
import { Link } from "react-router-dom";
import axios from "axios";
import ResumePreview from "./ResumePreview";
import html2pdf from 'html2pdf.js';
import { apiUrl } from "../config/apiBase";
import "./Sidebar.css";

const Sidebar = ({ activeItem }) => {
  const navigate = useNavigate();
  const { user, signOut, refreshTokenIfNeeded } = useAuth();
  const { isPaidUser, getRemainingQuestions, getUsageInfo, getResumeUsageInfo } = useSubscription();
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentHrSets, setRecentHrSets] = useState([]);
  const [recentResumes, setRecentResumes] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [resumeUsageInfo, setResumeUsageInfo] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Function to navigate to pricing section
  const navigateToPricing = () => {
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  const [hasFetchedJobs, setHasFetchedJobs] = useState(false);

  // Adjust sidebar width based on whether upgrade button is shown
  useEffect(() => {
    const shouldShowUpgradeButton = getRemainingQuestions() === 0 || 
      (() => {
        const scratchUsed = resumeUsageInfo?.scratchUsed || 0;
        const scratchLimit = resumeUsageInfo?.scratchLimit || 1;
        const tailoredUsed = resumeUsageInfo?.tailoredUsed || 0;
        const tailoredLimit = resumeUsageInfo?.tailoredLimit || 2;
        return (scratchUsed >= scratchLimit) || (tailoredUsed >= tailoredLimit);
      })();

    // Set width: 250px when no upgrade button, 280px when upgrade button is shown
    const newWidth = shouldShowUpgradeButton ? 280 : 250;
    const newMargin = shouldShowUpgradeButton ? 290 : 260;
    
    document.documentElement.style.setProperty('--sidebar-width', `${newWidth}px`);
    document.documentElement.style.setProperty('--main-content-margin', `${newMargin}px`);
  }, [usageInfo, resumeUsageInfo, getRemainingQuestions]);
  const popupRef = useRef(null);
  const historyRef = useRef(null);

  const getUserInitials = () => {
    if (!user) return "U";
    
    // Try to get full name from user metadata
    const fullName = user.user_metadata?.full_name || 
                    user.user_metadata?.name || 
                    user.email;
    
    if (!fullName) return "U";
    
    // Split the name and get first letters of first and last name
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0][0].toUpperCase();
    }
    
    return "U";
  };

  const getUserFullName = () => {
    if (!user) return "User";
    
    // Try to get full name from user metadata
    const fullName = user.user_metadata?.full_name || 
                    user.user_metadata?.name;
    
    if (!fullName && user.email) {
      // Extract name from email if no full name available
      return user.email.split('@')[0];
    }
    
    return fullName || "User";
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
      setShowLogoutPopup(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toggleLogoutPopup = () => {
    setShowLogoutPopup(!showLogoutPopup);
  };

  const toggleHistoryDropdown = () => {
    setShowHistoryDropdown(!showHistoryDropdown);
  };

  const fetchUsageInfo = useCallback(async () => {
    if (!user || loadingUsage) return;
    
    setLoadingUsage(true);
    try {
      const [usageData, resumeData] = await Promise.all([
        getUsageInfo(),
        getResumeUsageInfo()
      ]);
      
      setUsageInfo(usageData);
      setResumeUsageInfo(resumeData);
    } catch (err) {
      console.error('Error fetching usage info:', err);
    } finally {
      setLoadingUsage(false);
    }
  }, [user, loadingUsage, getUsageInfo, getResumeUsageInfo]);

  const fetchRecentJobs = useCallback(async () => {
    if (!user || loadingHistory) return;
    
    setLoadingHistory(true);
    try {
      // Get the current session to retrieve the access token
      const token = await refreshTokenIfNeeded();
      
      if (!token) {
        console.error('No access token available');
        setLoadingHistory(false);
        return;
      }

      // Fetch technical interview jobs
      const jobsResponse = await axios.get(apiUrl('/api/jobs'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Get only the first 3 most recent completed jobs (sidebar preview)
      const completedJobs = (jobsResponse.data.jobs || [])
        .filter(job => job.status === 'completed' && job.id)
        .slice(0, 3);
      
      setRecentJobs(completedJobs);

      // Fetch HR question sets
      const hrResponse = await axios.get(apiUrl('/api/hr-screening/history'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Get only the first 3 most recent HR question sets (sidebar preview)
      const recentHrSets = (hrResponse.data.hr_question_sets || [])
        .filter(hrSet => hrSet.id)
        .slice(0, 3);
      
      setRecentHrSets(recentHrSets);

      // Fetch resumes
      const resumesResponse = await axios.get(apiUrl('/api/resume-builder/resumes'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Get only the first 3 most recent resumes (sidebar preview)
      const recentResumes = (resumesResponse.data.resumes || [])
        .slice(0, 3);
      
      setRecentResumes(recentResumes);
      
    } catch (err) {
      console.error('Error fetching recent history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [user, loadingHistory, refreshTokenIfNeeded]);

  const getJobTitle = (job) => {
    // Try to get title from images first, then fallback to URL
    if (job.images && job.images.length > 0 && job.images[0].job_title) {
      const image = job.images[0];
      return `${image.job_title}${image.company ? ` at ${image.company}` : ''}`;
    }
    // Extract title from URL if no image data
    return job.job_url ? `Job from ${new URL(job.job_url).hostname}` : 'Untitled Job';
  };

  const getResumeTitle = (resume) => {
    // Try to get name from resume data, fallback to generic title
    if (resume.personal_info && resume.personal_info.fullName) {
      return `${resume.personal_info.fullName}'s Resume`;
    }
    return `Resume - ${formatDate(resume.created_at)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowLogoutPopup(false);
      }
      if (historyRef.current && !historyRef.current.contains(event.target)) {
        setShowHistoryDropdown(false);
      }
    };

    if (showLogoutPopup || showHistoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLogoutPopup, showHistoryDropdown]);

  // Reset fetch flag when user changes
  useEffect(() => {
    setHasFetchedJobs(false);
    setRecentJobs([]);
    setRecentHrSets([]);
    setRecentResumes([]);
  }, [user?.id]);

  // Fetch usage info when component mounts or user changes
  useEffect(() => {
    fetchUsageInfo();
  }, [fetchUsageInfo]);

  // Fetch recent jobs only when dropdown opens and we haven't fetched yet
  useEffect(() => {
    if (showHistoryDropdown && user && !hasFetchedJobs && !loadingHistory) {
      fetchRecentJobs();
      setHasFetchedJobs(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistoryDropdown, user, hasFetchedJobs, loadingHistory]);

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <Link to="/" aria-label="LeetEng Home">
            <img
              src="/Leet Logo.png"
              alt="LeetEng Logo"
              className="logo-image2"
            />
          </Link>
      </div>
      <nav className="sidebar-nav">
        <button
          className={`nav-items ${activeItem === 'new-question' ? 'active' : ''}`}
          onClick={() => navigate('/dashboard')}
        >
          New Question
        </button>
        <button
          className={`nav-items ${activeItem === 'ai-resume-builder' ? 'active' : ''}`}
          onClick={() => navigate('/dashboard/ai-resume-builder')}
        >
          AI Resume Builder
        </button>
        <div className="history-section" ref={historyRef}>
          <button
            className={`nav-items ${activeItem === 'history' ? 'active' : ''} ${showHistoryDropdown ? 'dropdown-open' : ''}`}
            onClick={() => {
              toggleHistoryDropdown();
            }}
          >
            History
            <FiChevronRight 
              size={14} 
              className={`history-chevron ${showHistoryDropdown ? 'rotated' : ''}`}
            />
          </button>
          
          {showHistoryDropdown && (
            <div className="sidebar-history-dropdown">
              {loadingHistory ? (
                <div className="sidebar-history-loading">Loading...</div>
              ) : (recentJobs.length > 0 || recentHrSets.length > 0 || recentResumes.length > 0) ? (
                <div className="sidebar-history-items">
                  {/* Technical Interview Jobs */}
                  {recentJobs.map((job, index) => (
                    <div 
                      key={job.id || `job-${index}`} 
                      className="sidebar-history-item sidebar-history-item-technical"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (job.id) {
                          try {
                            navigate(`/questions?job_id=${job.id}`);
                            setShowHistoryDropdown(false);
                          } catch (error) {
                            console.error('Navigation failed:', error);
                          }
                        } else {
                          console.error('Job missing ID:', job);
                        }
                      }}
                    >
                      <div className="sidebar-history-item-type sidebar-history-item-type-technical">Technical</div>
                      <div className="sidebar-history-item-title">
                        {getJobTitle(job)}
                      </div>
                      <div className="sidebar-history-item-date">
                        {formatDate(job.created_at)}
                      </div>
                    </div>
                  ))}

                  {/* HR Question Sets */}
                  {recentHrSets.map((hrSet, index) => (
                    <div 
                      key={hrSet.id || `hr-${index}`} 
                      className="sidebar-history-item sidebar-history-item-hr"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (hrSet.id) {
                          try {
                            navigate(`/hr-questions?session_id=${hrSet.id}`);
                            setShowHistoryDropdown(false);
                          } catch (error) {
                            console.error('Navigation failed:', error);
                          }
                        } else {
                          console.error('HR set missing ID:', hrSet);
                        }
                      }}
                    >
                      <div className="sidebar-history-item-type sidebar-history-item-type-hr">HR Screening</div>
                      <div className="sidebar-history-item-title">
                        {`${hrSet.title}${hrSet.company ? ` at ${hrSet.company}` : ''}`}
                      </div>
                      <div className="sidebar-history-item-date">
                        {formatDate(hrSet.created_at)}
                      </div>
                      {hrSet.interview_performance_scoring && (
                        <div className="sidebar-history-item-score">
                          <span className="sidebar-history-item-score-label">Score:</span>
                          <span className="sidebar-history-item-score-value">{hrSet.interview_performance_scoring.overall_performance_score}/100</span>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Recent Resumes */}
                  {recentResumes.map((resume, index) => (
                    <div 
                      key={resume.id || `resume-${index}`} 
                      className="sidebar-history-item sidebar-history-item-resume"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (resume.id) {
                          try {
                            // Construct resume_data from individual fields
                            const resumeData = {
                              personalInfo: resume.personal_info || {},
                              professionalSummary: resume.professional_summary || '',
                              experience: resume.experience || [],
                              education: resume.education || [],
                              skills: resume.skills || ''
                            };

                            if (!resumeData || (!resumeData.personalInfo && !resumeData.professionalSummary && 
                                (!resumeData.experience || resumeData.experience.length === 0) && 
                                (!resumeData.education || resumeData.education.length === 0) && !resumeData.skills)) {
                              alert('Resume data not available for download');
                              setShowHistoryDropdown(false);
                              return;
                            }

                            // Create a temporary container to render the ResumePreview component
                            const tempContainer = document.createElement('div');
                            tempContainer.style.position = 'absolute';
                            tempContainer.style.left = '-9999px';
                            tempContainer.style.top = '0';
                            document.body.appendChild(tempContainer);

                            // Use ReactDOM to render the ResumePreview component
                            const { createRoot } = await import('react-dom/client');
                            const root = createRoot(tempContainer);
                            
                            // Render the resume preview
                            await new Promise((resolve) => {
                              root.render(
                                <div className="pdf-resume-container">
                                  <ResumePreview 
                                    personalInfo={resumeData.personalInfo}
                                    professionalSummary={resumeData.professionalSummary}
                                    experience={resumeData.experience}
                                    education={resumeData.education}
                                    skills={resumeData.skills}
                                    showDownloadButton={false}
                                  />
                                </div>
                              );
                              // Give React time to render
                              setTimeout(resolve, 100);
                            });

                            // Get the rendered element
                            const resumeElement = tempContainer.querySelector('.pdf-resume-container');

                            if (!resumeElement) {
                              alert('Failed to render resume for download');
                              document.body.removeChild(tempContainer);
                              setShowHistoryDropdown(false);
                              return;
                            }

                            const opt = {
                              margin: [0.2, 0.2, 0.2, 0.2], // 0.4 inch margins for professional look
                              filename: `${resumeData.personalInfo?.fullName || 'Resume'}_Resume.pdf`,
                              image: { 
                                type: 'jpeg', 
                                quality: 0.98 
                              },
                              html2canvas: { 
                                scale: 2, // High quality rendering
                                useCORS: true,
                                letterRendering: true,
                                logging: false,
                                backgroundColor: '#ffffff',
                                width: resumeElement.scrollWidth,
                                height: resumeElement.scrollHeight,
                                windowWidth: resumeElement.scrollWidth,
                                windowHeight: resumeElement.scrollHeight,
                                x: 0,
                                y: 0
                              },
                              jsPDF: { 
                                unit: 'in', 
                                format: 'a4', 
                                orientation: 'portrait',
                                compress: true
                              },
                              pagebreak: { 
                                mode: ['avoid-all', 'css', 'legacy'],
                                before: '.page-break-before',
                                after: '.page-break-after',
                                avoid: '.avoid-break'
                              }
                            };

                            // Generate and download PDF with blank page removal
                            try {
                              const worker = html2pdf().set(opt).from(resumeElement);
                              const pdf = await worker.toPdf().get('pdf');
                              const pageCount = pdf.internal.getNumberOfPages();
                              
                              // Remove blank last page if detected
                              if (pageCount > 1) {
                                pdf.setPage(pageCount);
                                const lastPageHeight = pdf.internal.pageSize.getHeight();
                                
                                if (resumeElement.scrollHeight < (pageCount - 0.5) * lastPageHeight * 96) {
                                  pdf.deletePage(pageCount);
                                  console.log('Removed blank page');
                                }
                              }
                              
                              await worker.save();
                              setShowHistoryDropdown(false);
                            } catch (error) {
                              console.error('PDF generation error:', error);
                              alert('Failed to generate PDF. Please try again.');
                            } finally {
                              // Clean up
                              root.unmount();
                              document.body.removeChild(tempContainer);
                            }
                          } catch (error) {
                            console.error('Resume download failed:', error);
                            alert('Failed to download resume. Please try again.');
                            setShowHistoryDropdown(false);
                          }
                        } else {
                          console.error('Resume missing ID:', resume);
                        }
                      }}
                    >
                      <div className="sidebar-history-item-type sidebar-history-item-type-resume">Resume</div>
                      <div className="sidebar-history-item-title">
                        {getResumeTitle(resume)}
                      </div>
                      <div className="sidebar-history-item-date">
                        {formatDate(resume.created_at)}
                      </div>
                    </div>
                  ))}

                  <div className="sidebar-history-view-all">
                    <button 
                      className="sidebar-view-all-btn"
                      onClick={() => {
                        navigate('/dashboard/history');
                        setShowHistoryDropdown(false);
                      }}
                    >
                      View All History
                    </button>
                  </div>
                </div>
              ) : (
                <div className="sidebar-history-empty">No recent activity</div>
              )}
            </div>
          )}
        </div>
        <div className="usage-section">
          <div className="usage-display-combined">
            <div className="usage-header">
              <span className="usage-title">Usage Limits</span>
              {isPaidUser() && <FaCrown className="crown-icon" />}
            </div>
            
            {isPaidUser() ? (
              <>
                <div className="usage-unlimited-message">
                  <div className="unlimited-badge">✨ Standard Plan</div>
                </div>

                {/* Questions Section for Paid Users */}
                <div className="usage-item">
                  <div className="usage-item-header">
                    <FaQuestionCircle className="usage-item-icon" />
                    <span className="usage-item-label">HR & Technical Questions</span>
                  </div>
                  <div className="usage-stats">
                    <span className="usage-remaining">{getRemainingQuestions()} left</span>
                    <span className="usage-total">{(() => {
                      return `${usageInfo?.used || 0}/50`;
                    })()}</span>
                  </div>
                  <div className="usage-bar">
                    <div 
                      className="usage-progress" 
                      style={{ 
                        width: `${(() => {
                          return ((usageInfo?.used || 0) / 50) * 100;
                        })()}%` 
                      }}
                    ></div>
                  </div>
                </div>

                {/* Resume Section for Paid Users */}
                <div className="usage-item">
                  <div className="usage-item-header">
                    <FaQuestionCircle className="usage-item-icon" />
                    <span className="usage-item-label">ATS Resumes</span>
                  </div>
                  {(() => {
                    const tailoredUsed = resumeUsageInfo?.tailoredUsed || 0;
                    const tailoredLimit = 50;
                    
                    return (
                      <>
                        <div className="usage-sub-item">
                          <span className="usage-sub-label">From Scratch</span>
                          <span className="usage-remaining unlimited-text-small">Unlimited</span>
                        </div>
                        <div className="usage-bar usage-bar-small">
                          <div 
                            className="usage-progress usage-progress-unlimited" 
                            style={{ width: '100%' }}
                          ></div>
                        </div>
                        
                        <div className="usage-sub-item">
                          <span className="usage-sub-label">Job-Tailored</span>
                          <span className="usage-remaining">{tailoredUsed}/50</span>
                        </div>
                        <div className="usage-bar usage-bar-small">
                          <div 
                            className="usage-progress" 
                            style={{ width: `${(tailoredUsed / tailoredLimit) * 100}%` }}
                          ></div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </>
            ) : (
              <>
                {/* Questions Section */}
                <div className="usage-item">
                  <div className="usage-item-header">
                    <FaQuestionCircle className="usage-item-icon" />
                    <span className="usage-item-label">HR & Technical Questions</span>
                  </div>
                  <div className="usage-stats">
                    <span className="usage-remaining">{getRemainingQuestions()} left</span>
                    <span className="usage-total">{(() => {
                      return `${usageInfo?.used || 0}/${usageInfo?.limit || 0}`;
                    })()}</span>
                  </div>
                  <div className="usage-bar">
                    <div 
                      className="usage-progress" 
                      style={{ 
                        width: `${(() => {
                          return ((usageInfo?.used || 0) / (usageInfo?.limit || 1)) * 100;
                        })()}%` 
                      }}
                    ></div>
                  </div>
                </div>

                {/* Resume Section */}
                <div className="usage-item">
                  <div className="usage-item-header">
                    <FaQuestionCircle className="usage-item-icon" />
                    <span className="usage-item-label">ATS Resumes</span>
                  </div>
                  {(() => {
                    const scratchUsed = resumeUsageInfo?.scratchUsed || 0;
                    const scratchLimit = resumeUsageInfo?.scratchLimit || 1;
                    const tailoredUsed = resumeUsageInfo?.tailoredUsed || 0;
                    const tailoredLimit = resumeUsageInfo?.tailoredLimit || 2;
                    
                    return (
                      <>
                        <div className="usage-sub-item">
                          <span className="usage-sub-label">From Scratch</span>
                          <span className="usage-remaining">{scratchUsed}/{scratchLimit}</span>
                        </div>
                        <div className="usage-bar usage-bar-small">
                          <div 
                            className="usage-progress" 
                            style={{ width: `${scratchLimit === 999 ? 100 : (scratchUsed / scratchLimit) * 100}%` }}
                          ></div>
                        </div>
                        
                        <div className="usage-sub-item">
                          <span className="usage-sub-label">Job-Tailored</span>
                          <span className="usage-remaining">{tailoredUsed}/{tailoredLimit}</span>
                        </div>
                        <div className="usage-bar usage-bar-small">
                          <div 
                            className="usage-progress" 
                            style={{ width: `${(tailoredUsed / tailoredLimit) * 100}%` }}
                          ></div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Upgrade Button */}
                {(getRemainingQuestions() === 0 || 
                  (() => {
                    const scratchUsed = resumeUsageInfo?.scratchUsed || 0;
                    const scratchLimit = resumeUsageInfo?.scratchLimit || 1;
                    const tailoredUsed = resumeUsageInfo?.tailoredUsed || 0;
                    const tailoredLimit = resumeUsageInfo?.tailoredLimit || 2;
                    return (scratchUsed >= scratchLimit) || (tailoredUsed >= tailoredLimit);
                  })()) && (
                  <button 
                    className="upgrade-btn-combined"
                    onClick={navigateToPricing}
                  >
                    Upgrade to Standard Plan
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="user-sectiona">
          <div className="user-info" onClick={toggleLogoutPopup}>
            <div className="user-avatar">
              {getUserInitials()}
            </div>
            <div className="user-details">
              <p className="user-name">{getUserFullName()}</p>
            </div>
            <div className="user-menu-icon">
              <FiChevronDown size={12} />
            </div>
          </div>
          
          {showLogoutPopup && (
            <div className="user-dropdown1">
              <button 
                className="logout-button1"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
